/**
 * CommunityTopic — detail page for a single forum topic. Renders the topic
 * body, a poll or event card if applicable, and the reply thread (flat with
 * one level of nesting via "Reply to this"). Admins get pin / lock / delete
 * controls.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import ToolSelector from '../components/ToolSelector';
import ReactionBar from '../components/community/ReactionBar';
import ForumImageUploader from '../components/community/ForumImageUploader';
import AuthorProfileSidebar from '../components/community/AuthorProfileSidebar';
import ForumSubNav from '../components/community/ForumSubNav';
import CommunityAd from '../components/community/CommunityAd';
import AdSlot from '../components/AdSlot';
import AIAmbassadorPanel from '../components/community/AIAmbassadorPanel';
import UnderReviewBadge from '../components/community/UnderReviewBadge';
import { renderForumBody } from '../components/community/renderForumBody';
import AutoTranslatedForumBody from '../components/community/AutoTranslatedForumBody';
import { AutoTranslated } from '../components/i18n/AutoTranslated';
import AskExpertButton from '../components/AskExpertButton';
import TranslateButton from '../components/TranslateButton';
import ReportContentModal from '../components/ReportContentModal';
import {
  ArrowLeft, Pin, Lock, Trash2, Bookmark, BookmarkCheck, Reply,
  Calendar, MapPin, Link as LinkIcon, BarChart3, CheckCircle2, MessageSquare, Megaphone,
  Archive, Sparkles, Send, Flag, ShieldAlert,
} from 'lucide-react';

const relTime = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
};

const AvatarInitials = ({ name, color = '#0EA5E9', size = 32 }) => (
  <div
    className="rounded-full flex items-center justify-center font-bold flex-shrink-0"
    style={{
      backgroundColor: `${color}25`, color, width: size, height: size,
      fontSize: Math.max(10, Math.floor(size * 0.35)),
    }}
    aria-hidden="true"
  >
    {(name || '?').slice(0, 2).toUpperCase()}
  </div>
);

// -------------------------------------------------------------------------
// Poll card — shows options with vote counts. Voting updates in place.
// -------------------------------------------------------------------------
const PollCard = ({ topic, myVote, onVote }) => {
  const totalVotes = (topic.poll_options || []).reduce((s, o) => s + (o.votes || 0), 0);
  const [pending, setPending] = useState(false);
  const [selection, setSelection] = useState(myVote?.option_indices || []);

  useEffect(() => { setSelection(myVote?.option_indices || []); }, [myVote]);

  const toggle = (i) => {
    if (topic.poll_multi) {
      setSelection((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]);
    } else {
      setSelection([i]);
    }
  };

  const submit = async () => {
    if (!selection.length) { toast.error('Pick at least one option'); return; }
    setPending(true);
    try {
      await onVote(selection);
      toast.success('Vote saved');
    } finally { setPending(false); }
  };

  return (
    <div className="rounded-2xl border border-violet-500/30 bg-violet-500/5 p-4" data-testid="poll-card">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-violet-600" />
        <span className="text-[10px] uppercase tracking-widest font-bold text-violet-700">
          Poll{topic.poll_multi ? ' · multi-select' : ''}
        </span>
        <span className="ml-auto text-[11px] font-bold text-muted-foreground">
          {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
        </span>
      </div>
      <div className="space-y-2">
        {(topic.poll_options || []).map((o, i) => {
          const pct = totalVotes > 0 ? Math.round((o.votes / totalVotes) * 100) : 0;
          const chosen = selection.includes(i);
          return (
            <button
              key={i}
              type="button"
              onClick={() => toggle(i)}
              disabled={pending}
              data-testid={`poll-option-${i}`}
              className={`w-full text-left rounded-lg border p-2.5 transition-colors relative overflow-hidden
                ${chosen ? 'border-violet-500 bg-violet-500/10' : 'border-border bg-background hover:border-violet-400'}`}
            >
              {/* Progress fill */}
              <div className="absolute inset-y-0 left-0 bg-violet-500/15 pointer-events-none" style={{ width: `${pct}%` }} aria-hidden="true" />
              <div className="relative flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`h-4 w-4 rounded-${topic.poll_multi ? 'sm' : 'full'} border-2 flex items-center justify-center flex-shrink-0
                    ${chosen ? 'border-violet-600 bg-violet-600' : 'border-muted-foreground/40'}`}>
                    {chosen && <CheckCircle2 className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-sm font-semibold text-foreground truncate">{o.text}</span>
                </div>
                <div className="text-xs font-bold text-foreground whitespace-nowrap">{pct}% ({o.votes})</div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={pending} className="rounded-full font-bold" data-testid="poll-submit">
          {myVote ? 'Change vote' : 'Submit vote'}
        </Button>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------------
// Event card
// -------------------------------------------------------------------------
const EventCard = ({ topic }) => {
  const d = topic.event_starts_at ? new Date(topic.event_starts_at) : null;
  return (
    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="event-card">
      <div className="flex items-center gap-2 mb-2">
        <Calendar className="w-4 h-4 text-emerald-700" />
        <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Event</span>
      </div>
      <div className="flex items-start gap-4">
        {d && (
          <div className="flex flex-col items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-700 px-3 py-2 flex-shrink-0 min-w-[64px]">
            <span className="text-[10px] uppercase tracking-widest font-bold">{d.toLocaleDateString('en-US', { month: 'short' })}</span>
            <span className="text-2xl font-extrabold leading-none">{d.getDate()}</span>
            <span className="text-[10px] font-bold mt-0.5">{d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-1 text-sm">
          {topic.event_location && (
            <div className="flex items-center gap-1.5 text-foreground/85">
              <MapPin className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
              <span className="truncate">{topic.event_location}</span>
            </div>
          )}
          {topic.event_url && (
            <div className="flex items-center gap-1.5 text-primary">
              <LinkIcon className="w-3.5 h-3.5 flex-shrink-0" />
              <a href={topic.event_url} target="_blank" rel="noreferrer" className="underline truncate">{topic.event_url}</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------------
// Reply row (with optional one-level nested children)
// -------------------------------------------------------------------------
const ReplyRow = ({ reply, children, currentUser, onDelete, onReply, onDM, onReport, catColor, reactionData, flag }) => {
  const canDelete = currentUser && (currentUser.id === reply.author_id || currentUser.role === 'admin');
  const canDM = currentUser && reply.author_id && currentUser.id !== reply.author_id;
  return (
    <div
      className={`py-3 ${reply.is_admin_action ? 'rounded-lg border-2 border-rose-500/40 bg-rose-500/5 px-3 -mx-3' : ''}`}
      data-testid={`reply-${reply.id}`}
    >
      <div className="flex items-start gap-3">
        <AvatarInitials name={reply.author?.name} color={catColor} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className="font-bold text-foreground">{reply.author?.name || 'Unknown'}</span>
            {reply.author?.role === 'admin' && (
              <span className="text-[9px] uppercase tracking-widest font-bold bg-primary/10 text-primary px-1.5 py-0.5 rounded">Admin</span>
            )}
            {reply.is_admin_action && (
              <span
                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold bg-rose-500/15 text-rose-700 border border-rose-500/40 px-1.5 py-0.5 rounded"
                data-testid={`reply-admin-action-badge-${reply.id}`}
                title="Public moderator note — action was taken on this thread"
              >
                <ShieldAlert className="w-3 h-3" /> Admin action
              </span>
            )}
            <span className="text-muted-foreground">· {relTime(reply.created_at)}</span>
            {flag && <UnderReviewBadge flag={flag} />}
          </div>
          <div className="mt-1 text-sm text-foreground leading-relaxed">
            <AutoTranslatedForumBody text={reply.body_md} testid={`reply-body-${reply.id}`} />
          </div>
          <TranslateButton text={reply.body_md} testid={`reply-translate-${reply.id}`} />

          {/* Reactions + actions row */}
          {!reply.is_deleted && (
            <div className="mt-2 flex items-center flex-wrap gap-3">
              <ReactionBar
                targetType="reply"
                targetId={reply.id}
                initialCounts={reactionData?.counts}
                initialMyReaction={reactionData?.my_reaction}
                compact
              />
              <div className="flex items-center gap-3 text-[11px]">
                {onReply && (
                  <button
                    type="button"
                    onClick={() => onReply(reply)}
                    className="text-muted-foreground hover:text-primary font-semibold inline-flex items-center gap-1"
                    data-testid={`reply-reply-btn-${reply.id}`}
                  >
                    <Reply className="w-3 h-3" /> Reply to this
                  </button>
                )}
                {canDM && (
                  <button
                    type="button"
                    onClick={() => onDM(reply.author_id, reply.author?.name)}
                    className="text-muted-foreground hover:text-primary font-semibold inline-flex items-center gap-1"
                    data-testid={`reply-dm-btn-${reply.id}`}
                  >
                    <Send className="w-3 h-3" /> Message
                  </button>
                )}
                {onReport && (
                  <button
                    type="button"
                    onClick={() => onReport({ reply_id: reply.id, snippet: (reply.body_md || '').slice(0, 200) })}
                    className="text-muted-foreground hover:text-rose-600 font-semibold inline-flex items-center gap-1"
                    data-testid={`reply-report-btn-${reply.id}`}
                    title="Report to moderator"
                  >
                    <Flag className="w-3 h-3" /> Report
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => onDelete(reply)}
                    className="text-muted-foreground hover:text-destructive font-semibold inline-flex items-center gap-1"
                    data-testid={`reply-delete-btn-${reply.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                )}
              </div>
            </div>
          )}

          {children && children.length > 0 && (
            <div className="mt-2 ml-2 pl-3 border-l-2 border-border space-y-0">{children}</div>
          )}
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------------
// Main page
// -------------------------------------------------------------------------
export default function CommunityTopic() {
  const { API, user } = useContext(AuthContext);
  const { topic_id } = useParams();
  const navigate = useNavigate();

  const [topic, setTopic] = useState(null);
  const [replies, setReplies] = useState([]);
  const [myVote, setMyVote] = useState(null);
  const [reactions, setReactions] = useState({});    // { targetId: {counts, my_reaction, total} }
  const [flags, setFlags] = useState({ topic: null, replies: {} });   // { topic: flag|null, replies: { [reply_id]: flag } }
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState('');
  const [replyImages, setReplyImages] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [posting, setPosting] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);

  const isAdmin = user?.role === 'admin';

  const loadReactions = async (topicObj, replyList) => {
    // One bulk call for the topic; one for all replies.
    try {
      const topicSum = await axios.get(`${API}/forum/reactions/summary?target_type=topic&ids=${topicObj.id}`);
      const replyIds = (replyList || []).map((r) => r.id).join(',');
      const replySum = replyIds
        ? await axios.get(`${API}/forum/reactions/summary?target_type=reply&ids=${replyIds}`)
        : { data: {} };
      setReactions({ [topicObj.id]: topicSum.data[topicObj.id] || {}, ...replySum.data });
    } catch { /* silent */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/forum/topics/${topic_id}`);
      setTopic(r.data.topic);
      setReplies(r.data.replies || []);
      setMyVote(r.data.my_vote || null);
      await loadReactions(r.data.topic, r.data.replies || []);
      // Load AI flags for topic + all replies in one call
      try {
        const f = await axios.get(`${API}/forum/flags/topic/${topic_id}`);
        const all = f.data.flags || [];
        const topicFlag = all.find((x) => x.target_type === 'topic' && x.target_id === topic_id) || null;
        const replyFlags = {};
        for (const x of all) {
          if (x.target_type === 'reply') replyFlags[x.target_id] = x;
        }
        setFlags({ topic: topicFlag, replies: replyFlags });
      } catch { /* silent */ }
    } catch { toast.error('Topic not found'); navigate('/community'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [topic_id, API]);

  const catColor = topic?.category?.color || '#0EA5E9';

  // Build the flat + 1-level-deep reply tree.
  const threaded = useMemo(() => {
    const top = replies.filter((r) => !r.parent_id);
    const byParent = replies.reduce((acc, r) => {
      if (r.parent_id) (acc[r.parent_id] = acc[r.parent_id] || []).push(r);
      return acc;
    }, {});
    return top.map((t) => ({ ...t, children: byParent[t.id] || [] }));
  }, [replies]);

  const postReply = async () => {
    if (!replyBody.trim() && replyImages.length === 0) { toast.error('Write something first'); return; }
    setPosting(true);
    try {
      const imgMd = (replyImages || []).map((im) => `![${im.filename}](${im.url})`).join('\n');
      const combined = [replyBody.trim(), imgMd].filter(Boolean).join('\n\n');
      const payload = { body_md: combined };
      if (replyingTo) payload.parent_id = replyingTo.id;
      await axios.post(`${API}/forum/topics/${topic_id}/reply`, payload);
      setReplyBody('');
      setReplyImages([]);
      setReplyingTo(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reply');
    } finally { setPosting(false); }
  };

  const deleteReply = async (r) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await axios.delete(`${API}/forum/replies/${r.id}`);
      await load();
      toast.success('Reply deleted');
    } catch { toast.error('Could not delete'); }
  };

  const toggleBookmark = async () => {
    try {
      const r = await axios.post(`${API}/forum/topics/${topic_id}/bookmark`);
      setTopic((prev) => prev ? { ...prev, is_bookmarked: r.data.is_bookmarked } : prev);
    } catch { toast.error('Could not update bookmark'); }
  };

  const togglePin = async () => {
    try {
      const r = await axios.post(`${API}/forum/topics/${topic_id}/pin`);
      setTopic((prev) => prev ? { ...prev, is_pinned: r.data.is_pinned } : prev);
      toast.success(r.data.is_pinned ? 'Pinned' : 'Unpinned');
    } catch { toast.error('Failed'); }
  };

  const toggleLock = async () => {
    try {
      const r = await axios.post(`${API}/forum/topics/${topic_id}/lock`);
      setTopic((prev) => prev ? { ...prev, is_locked: r.data.is_locked } : prev);
      toast.success(r.data.is_locked ? 'Locked' : 'Unlocked');
    } catch { toast.error('Failed'); }
  };

  const toggleArchive = async () => {
    try {
      const r = await axios.post(`${API}/forum/topics/${topic_id}/archive`);
      setTopic((prev) => prev ? { ...prev, is_archived: r.data.is_archived } : prev);
      toast.success(r.data.is_archived ? 'Archived' : 'Moved to Live');
    } catch { toast.error('Failed'); }
  };

  const reviveTopic = async () => {
    try {
      await axios.post(`${API}/forum/topics/${topic_id}/revive`);
      setTopic((prev) => prev ? { ...prev, is_archived: false } : prev);
      toast.success('Topic revived — back in the Live feed');
    } catch { toast.error('Could not revive topic'); }
  };

  // One-click DM entry from any author avatar / reply. Opens (or creates)
  // the 1:1 conversation and navigates to /messages/<id>.
  const startDMWith = async (peerId, peerName) => {
    if (!peerId || peerId === user?.id) return;
    try {
      const r = await axios.post(`${API}/dm/conversations`, { peer_id: peerId });
      navigate(`/messages/${r.data.conversation.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not message ${peerName || 'this member'}`);
    }
  };

  const deleteTopic = async () => {
    if (!window.confirm('Delete this topic? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/forum/topics/${topic_id}`);
      toast.success('Topic deleted');
      navigate('/community');
    } catch { toast.error('Failed'); }
  };

  const votePoll = async (option_indices) => {
    const r = await axios.post(`${API}/forum/topics/${topic_id}/vote`, { option_indices });
    setTopic((prev) => prev ? { ...prev, poll_options: r.data.poll_options } : prev);
    setMyVote({ option_indices: r.data.option_indices });
  };

  if (loading || !topic) {
    return (
      <div className="min-h-screen bg-background">
        <ToolSelector />
        <div className="max-w-4xl mx-auto px-4 py-10 text-center text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const canModifyTopic = isAdmin || (user && user.id === topic.author_id);

  return (
    <div className="min-h-screen bg-background">
      <ToolSelector />
      <ForumSubNav activeCategorySlug={topic.category?.slug} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* MAIN */}
          <div className="lg:col-span-8">
            {/* Back + category */}
            <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Link to="/community" className="inline-flex items-center gap-1 font-bold text-primary hover:underline">
            <ArrowLeft className="w-3.5 h-3.5" /> Community
          </Link>
          {topic.category && (
            <>
              <span>·</span>
              <button
                type="button"
                onClick={() => navigate(`/community?tab=latest&cat=${topic.category.id}`)}
                className="inline-flex items-center gap-1 font-bold hover:text-foreground"
                style={{ color: catColor }}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: catColor }} />
                {topic.category.name}
              </button>
            </>
          )}
        </div>

        {/* Title + meta */}
        <div className="mb-4">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            {topic.is_pinned && <Pin className="w-4 h-4 text-amber-500" />}
            {topic.is_locked && <Lock className="w-4 h-4 text-muted-foreground" />}
            {topic.is_archived && (
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-600 bg-slate-500/15 border border-slate-500/30 rounded px-2 py-0.5 inline-flex items-center gap-1">
                <Archive className="w-3 h-3" /> Archived
              </span>
            )}
            {topic.kind === 'announcement' && (
              <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-500/15 border border-amber-500/30 rounded px-2 py-0.5 inline-flex items-center gap-1">
                <Megaphone className="w-3 h-3" /> Announcement
              </span>
            )}
            {flags.topic && <UnderReviewBadge flag={flags.topic} size="lg" />}
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-foreground leading-tight" data-testid="topic-title">
            <AutoTranslated as="span" testid="topic-title-text" showBadge={false}>{topic.title}</AutoTranslated>
          </h1>
          <div className="mt-2 flex items-center gap-3 text-xs flex-wrap">
            <AvatarInitials name={topic.author?.name} color={catColor} size={28} />
            <span className="font-bold text-foreground">{topic.author?.name || 'Unknown'}</span>
            <span className="text-muted-foreground">· {relTime(topic.created_at)}</span>
            <span className="text-muted-foreground">· {topic.view_count} views · {topic.reply_count} replies</span>
            {user && topic.author_id && user.id !== topic.author_id && (
              <button
                type="button"
                onClick={() => startDMWith(topic.author_id, topic.author?.name)}
                className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 text-primary px-2.5 h-6 text-[11px] font-bold hover:bg-primary/10 transition-colors"
                data-testid="topic-author-dm-btn"
              >
                <Send className="w-3 h-3" /> Message author
              </button>
            )}
            <AskExpertButton
              context={{
                type:    'discussion',
                id:      topic.id,
                title:   topic.title,
                snippet: (topic.body_md || '').slice(0, 800),
              }}
              variant="ghost"
              className="!text-[11px]"
              label="Ask an Expert"
            />
          </div>
        </div>

        {/* Archived banner — offer a one-click Revive to any member */}
        {topic.is_archived && (
          <div
            className="mb-4 rounded-xl border border-slate-500/30 bg-slate-500/10 px-4 py-3 flex items-center gap-3 flex-wrap"
            data-testid="topic-archived-banner"
          >
            <Archive className="w-4 h-4 text-slate-600 flex-shrink-0" />
            <div className="text-sm text-foreground flex-1 min-w-0">
              This topic is archived.
              {topic.archived_reason === 'stale_21d' && (
                <span className="text-muted-foreground"> Auto-archived after 21 days of no activity.</span>
              )}
            </div>
            <Button
              size="sm"
              onClick={reviveTopic}
              className="rounded-full font-bold"
              data-testid="topic-revive-btn"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Revive this topic
            </Button>
          </div>
        )}

        {/* Actions */}
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleBookmark}
            className="rounded-full"
            data-testid="topic-bookmark-btn"
          >
            {topic.is_bookmarked
              ? <><BookmarkCheck className="w-3.5 h-3.5 mr-1 text-primary" /> Bookmarked</>
              : <><Bookmark className="w-3.5 h-3.5 mr-1" /> Bookmark</>}
          </Button>
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={togglePin} className="rounded-full" data-testid="topic-pin-btn">
                <Pin className="w-3.5 h-3.5 mr-1" /> {topic.is_pinned ? 'Unpin' : 'Pin'}
              </Button>
              <Button variant="outline" size="sm" onClick={toggleLock} className="rounded-full" data-testid="topic-lock-btn">
                <Lock className="w-3.5 h-3.5 mr-1" /> {topic.is_locked ? 'Unlock' : 'Lock'}
              </Button>
              <Button variant="outline" size="sm" onClick={toggleArchive} className="rounded-full" data-testid="topic-archive-btn">
                <Archive className="w-3.5 h-3.5 mr-1" /> {topic.is_archived ? 'Unarchive' : 'Archive'}
              </Button>
            </>
          )}
          {canModifyTopic && (
            <Button variant="outline" size="sm" onClick={deleteTopic} className="rounded-full text-destructive hover:text-destructive" data-testid="topic-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          )}
        </div>

        {/* Pinned moderator note — appears at the top of the topic when
            an admin has taken action + pinned a sticky note. */}
        {topic.admin_pinned_note && (
          <div
            className="mb-4 rounded-2xl border-2 border-rose-500/40 bg-rose-500/10 px-4 py-3 flex items-start gap-3"
            data-testid="topic-admin-pinned-note"
          >
            <div className="h-8 w-8 rounded-xl bg-rose-500/20 text-rose-700 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700">
                Moderator note · pinned{topic.admin_pinned_by ? ` by ${topic.admin_pinned_by}` : ''}
              </div>
              <div className="mt-0.5 text-sm text-foreground whitespace-pre-wrap">
                {topic.admin_pinned_note}
              </div>
            </div>
          </div>
        )}

        {/* Body / poll / event */}
        <div className="pro-card p-5 mb-6 space-y-4">
          {topic.body_md && <AutoTranslatedForumBody text={topic.body_md} testid="topic-body" />}
          {topic.body_md && <TranslateButton text={topic.body_md} testid="topic-translate" />}
          {topic.kind === 'poll'  && <PollCard topic={topic} myVote={myVote} onVote={votePoll} />}
          {topic.kind === 'event' && <EventCard topic={topic} />}
          {(topic.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border">
              {topic.tags.map((t) => (
                <span key={t} className="text-[10px] font-semibold text-muted-foreground bg-muted rounded px-1.5 py-0.5">{t}</span>
              ))}
            </div>
          )}

          {/* Reaction bar for the topic itself */}
          <div className="pt-2 border-t border-border flex items-center flex-wrap gap-3">
            <ReactionBar
              targetType="topic"
              targetId={topic.id}
              initialCounts={reactions[topic.id]?.counts}
              initialMyReaction={reactions[topic.id]?.my_reaction}
            />
            {user && topic.author_id !== user.id && (
              <button
                type="button"
                onClick={() => setReportTarget({
                  topic_id: topic.id,
                  snippet: `${(topic.title || '').slice(0, 80)} — ${(topic.body_md || '').slice(0, 120)}`,
                })}
                className="ml-auto text-xs font-semibold text-muted-foreground hover:text-rose-600 inline-flex items-center gap-1"
                data-testid="topic-report-btn"
                title="Report this topic to a moderator"
              >
                <Flag className="w-3 h-3" /> Report
              </button>
            )}
          </div>
        </div>

        {/* Replies */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-muted-foreground" />
            <h2 className="font-heading text-lg font-extrabold text-foreground">
              {topic.reply_count} {topic.reply_count === 1 ? 'reply' : 'replies'}
            </h2>
          </div>

          <div className="pro-card divide-y divide-border" data-testid="topic-replies">
            {threaded.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">No replies yet — start the conversation.</div>
            ) : threaded.map((r) => (
              <div key={r.id} className="px-4">
                <ReplyRow
                  reply={r}
                  currentUser={user}
                  onDelete={deleteReply}
                  onReply={topic.is_locked && !isAdmin ? null : setReplyingTo}
                  onDM={startDMWith}
                  onReport={setReportTarget}
                  catColor={catColor}
                  reactionData={reactions[r.id]}
                  flag={flags.replies[r.id]}
                >
                  {(r.children || []).map((child) => (
                    <ReplyRow
                      key={child.id}
                      reply={child}
                      currentUser={user}
                      onDelete={deleteReply}
                      onReply={null}    // no deeper nesting per your spec
                      onDM={startDMWith}
                      onReport={setReportTarget}
                      catColor={catColor}
                      reactionData={reactions[child.id]}
                      flag={flags.replies[child.id]}
                    />
                  ))}
                </ReplyRow>
              </div>
            ))}
          </div>

          {/* Composer */}
          {(topic.is_locked && !isAdmin) ? (
            <div className="mt-4 rounded-2xl border border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground" data-testid="topic-locked-notice">
              <Lock className="w-4 h-4 mx-auto mb-1" />
              This topic is locked. New replies are disabled.
            </div>
          ) : (
            <div className="mt-4 pro-card p-4">
              {replyingTo && (
                <div className="mb-2 text-xs font-bold text-primary flex items-center gap-2" data-testid="reply-to-indicator">
                  Replying to {replyingTo.author?.name || 'a comment'}
                  <button type="button" onClick={() => setReplyingTo(null)} className="text-muted-foreground hover:text-foreground">× cancel</button>
                </div>
              )}
              <Textarea
                rows={3}
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Add to the conversation…"
                data-testid="reply-input"
              />
              <div className="mt-2">
                <ForumImageUploader
                  value={replyImages}
                  onChange={setReplyImages}
                  testIdPrefix="reply-image"
                  compact
                />
              </div>
              <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                <AskExpertButton
                  variant="ghost"
                  className="!text-[11px]"
                  label={replyBody.trim() ? 'Ask an Expert (uses your draft)' : 'Ask an Expert first'}
                  context={{
                    type:    'discussion',
                    id:      topic.id,
                    title:   topic.title,
                    snippet: (replyBody || topic.body_md || '').slice(0, 800),
                  }}
                />
                <Button
                  onClick={postReply}
                  disabled={posting}
                  className="rounded-full font-bold"
                  data-testid="reply-submit-btn"
                >
                  {posting ? 'Posting…' : (replyingTo ? 'Post reply' : 'Post')}
                </Button>
              </div>
            </div>
          )}
        </div>
        {/* end MAIN */}
          </div>

          {/* SIDEBAR — Author profile card + related topics */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-20 space-y-4">
              <AIAmbassadorPanel
                mode="fact-check"
                topicId={topic.id}
                title="Fact Check with AI"
                subtitle="Compare this thread against Freeze Dry Academy knowledge."
              />
              <AIAmbassadorPanel
                mode="search"
                categoryId={topic.category?.id}
                title="AI Summarize and Research"
                subtitle="Search + summarize every post, live or archived."
                cta="Summarize & Research"
              />
              <AuthorProfileSidebar userId={topic.author_id} catColor={catColor} />
              <CommunityAd placement="topic" />
              <AdSlot location="community_side" variant="sidebar" />
            </div>
          </div>
        </div>
      </div>

      {/* Report-to-moderator modal (works for topic + any reply) */}
      <ReportContentModal
        open={!!reportTarget}
        onClose={() => setReportTarget(null)}
        target={reportTarget || {}}
      />
    </div>
  );
}
